/* /approvals — port of prototype pgApprovals/approvalsList (gaps.js). */
import { useMemo, useState } from "react";
import { useAcknowledgeResignation, useAllEmployees, useApprovals, useDecideExpense, useDecideLeave, useDecideLoan, useExpenses, useIssueLetter, useLeaves, useLetters, useLoans, useOffboarding, useToday } from "../../data/hooks";
import type { Approval, Employee } from "../../data/types";
import { addDays, daysTo } from "../../domain/dates";
import { useI18n } from "../../i18n";
import { gDate, moneyText } from "../../lib/format";
import { useSession } from "../../app/session";
import { Button, Chip, Confirm, EmptyState, ErrorBox, PageHeader, Person, Skeleton, Tabs, errMsg, useToast } from "../../ui";
import { canWriteHr } from "./components/shared";
import { curCode } from "../../lib/country";

type Kind = Approval["kind"];
const KINDS: Kind[] = ["Leave", "Expense", "Loan", "Letter", "Resignation"];
const KIND_AR: Record<Kind, [string, string]> = { Leave: ["إجازة", "إجازات"], Expense: ["مصروف", "مصروفات"], Loan: ["قرض", "قروض"], Letter: ["خطاب", "خطابات"], Resignation: ["استقالة", "استقالات"] };
const PLURAL_EN: Record<Kind, string> = { Leave: "Leave", Expense: "Expenses", Loan: "Loans", Letter: "Letters", Resignation: "Resignations" };

export default function Approvals() {
  const i = useI18n();
  const { t, L, lang } = i;
  const { role } = useSession();
  const toast = useToast();
  const today = useToday();
  const hr = canWriteHr(role);
  const [tab, setTab] = useState<"all" | Kind>("all");
  const [busy, setBusy] = useState<string | null>(null);
  const [low, setLow] = useState(false);

  const q = useApprovals();
  const empsQ = useAllEmployees();
  const leaves = useLeaves({ enabled: hr });
  const letters = useLetters({ enabled: hr });
  const offb = useOffboarding({ enabled: hr });
  const loans = useLoans();
  const expenses = useExpenses();

  const decideLeave = useDecideLeave();
  const decideLoan = useDecideLoan();
  const decideExpense = useDecideExpense();
  const issueLetter = useIssueLetter();
  const ackRes = useAcknowledgeResignation();

  const byId = useMemo(() => new Map((empsQ.data ?? []).map((e) => [e.id, e] as [number, Employee])), [empsQ.data]);

  if (q.error) return <ErrorBox error={q.error} onRetry={() => q.refetch()} />;
  if (!q.data || !empsQ.data) return <Skeleton />;
  const list = q.data;
  const shown = list.filter((x) => tab === "all" || x.kind === tab);

  /** Bilingual "what" + "when" built from the source record when we can read it; server summary otherwise. */
  const describe = (a: Approval): { what: string; when: string } => {
    if (a.kind === "Leave") {
      const l = leaves.data?.find((x) => x.id === a.refId);
      if (l) return { what: t(l.type) + " · " + l.days + " " + t("days"), when: gDate(l.from, lang) + " → " + gDate(l.to, lang) };
    }
    if (a.kind === "Loan") {
      const l = loans.data?.find((x) => x.id === a.refId);
      if (l) return { what: L(l.type, l.type === "Salary advance" ? "سلفة راتب" : "قرض شخصي") + " · " + moneyText(l.amount, lang, 0), when: l.months + " " + t("months") };
    }
    if (a.kind === "Letter") {
      const l = letters.data?.find((x) => x.id === a.refId);
      if (l) return { what: t(l.kind), when: l.to };
    }
    if (a.kind === "Expense") {
      const x = expenses.data?.find((v) => v.id === a.refId);
      if (x) return { what: x.vendor + " · " + moneyText(x.amount, lang), when: t(x.category) };
    }
    if (a.kind === "Resignation") {
      const o = offb.data?.find((v) => v.id === a.refId);
      if (o) return { what: L("Resignation · last day ", "استقالة · آخر يوم ") + gDate(o.lastDay, lang), when: L("deemed accepted ", "تعد مقبولة ") + gDate(addDays(o.submitted, 30), lang) };
    }
    return { what: a.summary, when: /^\d{4}-\d{2}-\d{2}$/.test(a.when) ? gDate(a.when, lang) : a.when };
  };

  const can = (k: Kind) => (k === "Loan" || k === "Expense" ? true : hr);

  const run = async (key: string, fn: () => Promise<unknown>, ok: string) => {
    setBusy(key);
    try { await fn(); toast(ok); } catch (e) { toast(errMsg(e), "bad"); } finally { setBusy(null); }
  };
  const decide = (a: Approval, approve: boolean) => {
    const e = byId.get(a.employeeId);
    const who = e ? i.name(e) : "#" + a.employeeId;
    const okMsg = approve ? L("Approved · " + who, "تم الاعتماد · " + who) : L("Rejected · " + who, "تم الرفض · " + who);
    const key = a.id + (approve ? ":ok" : ":no");
    if (a.kind === "Leave") return run(key, () => decideLeave.mutateAsync({ id: a.refId, approve }), okMsg);
    if (a.kind === "Loan") return run(key, () => decideLoan.mutateAsync({ id: a.refId, approve }), approve ? L("Loan approved · repayments start next payroll", "تم اعتماد القرض · يبدأ الخصم في المسير القادم") : okMsg);
    if (a.kind === "Expense") return run(key, () => decideExpense.mutateAsync({ id: a.refId, approve }), okMsg);
  };

  const lowRisk = list.filter((a) => {
    if (a.kind === "Expense") { const x = expenses.data?.find((v) => v.id === a.refId); return !!x && x.amount < 500; }
    if (a.kind === "Letter" && hr) { const l = letters.data?.find((v) => v.id === a.refId); return !!l && l.kind === "Salary certificate"; }
    return false;
  });
  const approveLow = async () => {
    setBusy("low");
    let n = 0;
    try {
      for (const a of lowRisk) {
        if (a.kind === "Expense") await decideExpense.mutateAsync({ id: a.refId, approve: true });
        else await issueLetter.mutateAsync(a.refId);
        n++;
      }
      toast(L(n + " low-risk requests approved (salary certificates, expenses under " + curCode() + " 500)", "تم اعتماد " + n + " طلبات منخفضة المخاطر"));
    } catch (e) { toast(errMsg(e), "bad"); } finally { setBusy(null); setLow(false); }
  };

  const dueChip = (a: Approval) => {
    const n = /^\d{4}-\d{2}-\d{2}$/.test(a.when) ? daysTo(today, addDays(a.when, a.slaDays)) : a.slaDays;
    if (n < 0) return <Chip tone="bad">{L("Overdue " + -n + "d", "متأخر " + -n + " يوم")}</Chip>;
    if (n <= 1) return <Chip tone="warn">{L("Today", "اليوم")}</Chip>;
    return <Chip tone="info">{L("in " + n + " days", "خلال " + n + " أيام")}</Chip>;
  };

  return (
    <div data-testid="approvals">
      <PageHeader
        title={L("Approvals", "الاعتمادات")}
        sub={L("Every request waiting for you, in one place. Oldest and most urgent first.", "كل الطلبات المنتظرة في مكان واحد، الأقدم والأكثر إلحاحًا أولًا.")}
        actions={<>
          <Button icon="users" onClick={() => toast(L("Approvals delegated to Mohammed Al-Zahrani until 10 Oct", "تم تفويض الاعتمادات إلى محمد الزهراني حتى 10 أكتوبر"))} data-testid="btn-delegate">{L("Delegate while away", "التفويض أثناء الغياب")}</Button>
          <Button variant="pri" icon="check" onClick={() => (lowRisk.length ? setLow(true) : toast(L("No low-risk requests waiting", "لا توجد طلبات منخفضة المخاطر"), "info"))} loading={busy === "low"} data-testid="btn-approve-low">{L("Approve all low-risk", "اعتماد الطلبات منخفضة المخاطر")}</Button>
        </>}
      />
      <section className="card">
        <Tabs<"all" | Kind>
          label={L("Request type", "نوع الطلب")}
          value={tab}
          onChange={setTab}
          items={[["all", t("All"), list.length], ...KINDS.map((k) => [k, L(PLURAL_EN[k], KIND_AR[k][1]), list.filter((x) => x.kind === k).length || null] as ["all" | Kind, string, number | null])]}
        />
        {shown.length ? (
          <div className="tw">
            <table className="tbl cards" data-testid="approvals-table">
              <thead><tr><th>{t("Employee")}</th><th>{t("Request")}</th><th className="hide-md">{t("Details")}</th><th>{L("Due", "الاستحقاق")}</th><th className="end">{t("Actions")}</th></tr></thead>
              <tbody>
                {shown.map((a) => {
                  const e = byId.get(a.employeeId);
                  const d = describe(a);
                  return (
                    <tr key={a.id} data-testid={"approval-" + a.id}>
                      <td className="k1">{e ? <Person e={e} /> : "#" + a.employeeId}</td>
                      <td className="k3" data-l={t("Request")}><Chip nodot>{L(a.kind, KIND_AR[a.kind][0])}</Chip> {d.what}</td>
                      <td className="hide-md sm muted">{d.when}</td>
                      <td className="k2">{dueChip(a)}</td>
                      <td className="end k3" data-l="">
                        <div className="row nw" style={{ justifyContent: "flex-end" }}>
                          {!can(a.kind) ? <span className="xs muted">{L("HR decides", "تقرره الموارد البشرية")}</span>
                            : a.kind === "Letter" ? <Button variant="sm" loading={busy === a.id} onClick={() => run(a.id, () => issueLetter.mutateAsync(a.refId), L("Letter issued", "تم إصدار الخطاب") + (e ? " · " + i.name(e) : ""))} data-testid={"btn-issue-" + a.refId}>{L("Issue", "إصدار")}</Button>
                            : a.kind === "Resignation" ? <Button variant="sm" loading={busy === a.id} onClick={() => run(a.id, () => ackRes.mutateAsync(a.refId), L("Resignation acknowledged · notice period running", "تم استلام الاستقالة · فترة الإشعار سارية"))} data-testid={"btn-ack-" + a.refId}>{L("Acknowledge", "استلام")}</Button>
                            : <>
                                <Button variant="ghost sm" loading={busy === a.id + ":no"} disabled={!!busy && busy.startsWith(a.id)} onClick={() => decide(a, false)} data-testid={"btn-reject-" + a.id}>{t("Reject")}</Button>
                                <Button variant="sm" loading={busy === a.id + ":ok"} disabled={!!busy && busy.startsWith(a.id)} onClick={() => decide(a, true)} data-testid={"btn-approve-" + a.id}>{t("Approve")}</Button>
                              </>}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <EmptyState title={L("You're all caught up", "لا توجد طلبات معلقة")} desc={L("New requests will show up here and in your notifications.", "ستظهر الطلبات الجديدة هنا وفي الإشعارات.")} />
        )}
      </section>
      {low && (
        <Confirm
          title={L("Approve low-risk requests", "اعتماد الطلبات منخفضة المخاطر")}
          body={<p>{L(lowRisk.length + " requests will be approved: salary certificates and expenses under " + curCode() + " 500.", "سيتم اعتماد " + lowRisk.length + " طلبات: شهادات الراتب والمصروفات الأقل من 500 ر.س.")}</p>}
          confirmLabel={t("Approve")}
          busy={busy === "low"}
          onConfirm={approveLow}
          onClose={() => setLow(false)}
        />
      )}
    </div>
  );
}
